// ════════════════════════════════════════════════════════════════════════════
//  features/fiscal/transporte/hooks/useAverbacao.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { averbacaoService } from '../services/averbacaoService';
import type { AverbarRequest } from '../types';

const KEYS = {
  all:      ['averbacao'] as const,
  historico: (p: number, t: number) => ['averbacao', 'historico', p, t] as const,
};

export const useAverbacao = (pagina = 1, tamanhoPagina = 20) => {
  const qc = useQueryClient();

  const { data: itens = [], isLoading } = useQuery({
    queryKey: KEYS.historico(pagina, tamanhoPagina),
    queryFn:  () => averbacaoService.historico(pagina, tamanhoPagina),
  });

  const averbar = useMutation({
    mutationFn: (req: AverbarRequest) => averbacaoService.averbar(req),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success(`Averbado — Protocolo: ${res.protocolo ?? '-'}`);
      else toast.warning(`Erro na averbação: ${res.mensagemErro ?? 'desconhecido'}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const cancelar = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => averbacaoService.cancelar(id, motivo),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      if (res.sucesso) toast.success('Averbação cancelada com sucesso');
      else toast.warning(`Erro ao cancelar: ${res.mensagemErro ?? 'desconhecido'}`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return { itens, isLoading, averbar, cancelar };
};
